import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

type RawRow = Record<string, unknown>;

const asNumber = (value: unknown): number | null => {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const getFirstNumber = (row: RawRow, keys: string[]): number | null => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) {
      const n = asNumber(row[key]);
      if (n != null) return n;
    }
  }
  return null;
};

const getFirstString = (row: RawRow, keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== '') return String(value);
  }
  return null;
};

const normalizeYear = (row: RawRow): number | null => {
  return getFirstNumber(row, ['year', 'analysis_year', 'vintage_year']);
};

export interface SolarPotentialPoint {
  year: number;
  technicalPotentialMw: number | null;
  economicPotentialMw: number | null;
  generationMwh: number | null;
  locationName: string | null;
}

export interface SolarPotentialResult {
  rows: SolarPotentialPoint[];
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: (number | null)[];
      borderColor: string;
      backgroundColor: string;
      borderWidth: number;
      tension: number;
      pointRadius: number;
      spanGaps: boolean;
      yAxisID?: string;
    }>;
  } | null;
  chartOptions: Record<string, unknown> | null;
  latestYear: number | null;
  latestTechnicalPotentialMw: number | null;
  latestEconomicPotentialMw: number | null;
  latestGenerationMwh: number | null;
}

type SolarPotentialChartDataset = NonNullable<SolarPotentialResult['chartData']>['datasets'][number];

export async function fetchSolarTechnicalPotential(
  geofips: number | string | null | undefined,
  locationName?: string
): Promise<SolarPotentialResult> {
  const empty: SolarPotentialResult = {
    rows: [],
    chartData: null,
    chartOptions: null,
    latestYear: null,
    latestTechnicalPotentialMw: null,
    latestEconomicPotentialMw: null,
    latestGenerationMwh: null,
  };

  const gRaw = geofips != null ? String(geofips).trim() : '';
  const gNormalized = gRaw ? (gRaw.replace(/^0+/, '') || '0') : '';

  if (!gNormalized) {
    return empty;
  }

  const padded7 = gNormalized.padStart(7, '0').slice(-7);
  const geoCandidates = Array.from(new Set([gNormalized, padded7]));

  let { data, error } = await supabaseClient
    .from('solar_technical_potential')
    .select(
      'geofips, year, location_name, technical_potential_mw, potential_generation_mwh, building_count, suitable_roof_area_sqft, source'
    )
    .in('geofips', geoCandidates);

  if (!error && (!data || data.length === 0)) {
    const geofipsNumber = Number(gNormalized);
    if (Number.isFinite(geofipsNumber)) {
      const retry = await supabaseClient
        .from('solar_technical_potential')
        .select(
          'geofips, year, location_name, technical_potential_mw, potential_generation_mwh, building_count, suitable_roof_area_sqft, source'
        )
        .eq('geofips', geofipsNumber);
      if (!retry.error && retry.data && retry.data.length > 0) {
        data = retry.data;
        error = retry.error;
      }
    }
  }

  if (error) {
    throw new Error(error.message || 'Failed to fetch solar technical potential');
  }

  const mapRows = (rows: RawRow[]): SolarPotentialPoint[] =>
    (rows || [])
      .map((row: RawRow) => {
        const year = normalizeYear(row);
        if (year == null) return null;
        return {
          year,
          technicalPotentialMw: getFirstNumber(row, ['technical_potential_mw', 'value']),
          // Column not present in current schema; keep null for UI compatibility.
          economicPotentialMw: getFirstNumber(row, ['economic_potential_mw']),
          generationMwh: getFirstNumber(row, ['potential_generation_mwh']),
          locationName: getFirstString(row, ['location_name', 'city', 'county']),
        };
      })
      .filter((row): row is SolarPotentialPoint => row != null)
      .sort((a, b) => a.year - b.year);

  let normalizedRows: SolarPotentialPoint[] = mapRows((data || []) as RawRow[]);

  // City geofips often have no rows; fall back to parent county (table is mostly county FIPS).
  if (normalizedRows.length === 0 && locationName) {
    await supabaseLocationService.initialize().catch(() => {});
    const countyName = supabaseLocationService.getCountyForCity(locationName);
    if (countyName) {
      const countyCandidatesNames = Array.from(
        new Set([countyName, countyName.replace(/\s+County$/i, ''), `${countyName.replace(/\s+County$/i, '')} County`])
      );
      let countyGeofips: number | null = null;
      for (const name of countyCandidatesNames) {
        const { data: popRows } = await supabaseClient
          .from('population')
          .select('geofips, geography_type, location_name')
          .eq('location_name', name)
          .eq('geography_type', 'County')
          .limit(1);
        if (popRows?.[0]?.geofips != null) {
          countyGeofips = Number(popRows[0].geofips);
          break;
        }
      }
      if (countyGeofips == null) {
        countyGeofips = await supabaseLocationService.getGeofips(countyCandidatesNames[0]);
      }
      if (countyGeofips != null) {
        const countyRaw = String(countyGeofips).trim();
        const countyNorm = countyRaw.replace(/^0+/, '') || '0';
        const countyPad5 = countyNorm.padStart(5, '0').slice(-5);
        const countyCandidates = Array.from(new Set([countyNorm, countyPad5, String(countyGeofips)]));
        const countyQuery = await supabaseClient
          .from('solar_technical_potential')
          .select(
            'geofips, year, location_name, technical_potential_mw, potential_generation_mwh, building_count, suitable_roof_area_sqft, source'
          )
          .in('geofips', countyCandidates);
        if (!countyQuery.error && countyQuery.data && countyQuery.data.length > 0) {
          normalizedRows = mapRows(countyQuery.data as RawRow[]);
        }
      }
    }
  }

  const dedupedRows = Array.from(
    new Map(normalizedRows.map((r) => [r.year, r])).values()
  ).sort((a, b) => a.year - b.year);

  const labels = dedupedRows.map((r) => r.year);
  const technicalData = dedupedRows.map((r) => r.technicalPotentialMw);
  const economicData = dedupedRows.map((r) => r.economicPotentialMw);
  const generationData = dedupedRows.map((r) => r.generationMwh);

  const hasTechnical = technicalData.some((v) => v != null);
  const hasEconomic = economicData.some((v) => v != null);
  const hasGeneration = generationData.some((v) => v != null);

  const datasets: SolarPotentialChartDataset[] = [];
  if (hasTechnical) {
    datasets.push({
      label: 'Technical potential (MW)',
      data: technicalData,
      borderColor: '#1d4ed8',
      backgroundColor: 'rgba(29, 78, 216, 0.15)',
      borderWidth: 2,
      tension: 0.2,
      pointRadius: 2,
      spanGaps: true,
      yAxisID: 'y',
    });
  }
  if (hasEconomic) {
    datasets.push({
      label: 'Economic potential (MW)',
      data: economicData,
      borderColor: '#0ea5e9',
      backgroundColor: 'rgba(14, 165, 233, 0.15)',
      borderWidth: 2,
      tension: 0.2,
      pointRadius: 2,
      spanGaps: true,
      yAxisID: 'y',
    });
  }
  if (hasGeneration) {
    datasets.push({
      label: 'Potential generation (MWh)',
      data: generationData,
      borderColor: '#16a34a',
      backgroundColor: 'rgba(22, 163, 74, 0.15)',
      borderWidth: 2,
      tension: 0.2,
      pointRadius: 2,
      spanGaps: true,
      yAxisID: hasTechnical || hasEconomic ? 'y1' : 'y',
    });
  }

  const chartData = labels.length && datasets.length
    ? { labels, datasets }
    : null;

  const chartOptions = chartData
    ? {
        type: 'line',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'top' },
          title: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Potential (MW)' },
          },
          ...(hasGeneration && (hasTechnical || hasEconomic)
            ? {
                y1: {
                  position: 'right',
                  beginAtZero: true,
                  grid: { drawOnChartArea: false },
                  title: { display: true, text: 'Potential generation (MWh)' },
                },
              }
            : {}),
          x: {
            title: { display: true, text: 'Year' },
          },
        },
      }
    : null;

  const latest = dedupedRows.length ? dedupedRows[dedupedRows.length - 1] : null;

  return {
    rows: dedupedRows,
    chartData,
    chartOptions,
    latestYear: latest?.year ?? null,
    latestTechnicalPotentialMw: latest?.technicalPotentialMw ?? null,
    latestEconomicPotentialMw: latest?.economicPotentialMw ?? null,
    latestGenerationMwh: latest?.generationMwh ?? null,
  };
}

